import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UserSelector } from '@/components/shared/UserSelector';
import { useInternetConnectionStore } from '@/stores/internetConnectionStore';
import { useSiteStore } from '@/stores/siteStore';
import { useZoneStore } from '@/stores/zoneStore';
import { useSystemStore } from '@/stores/systemStore';
import { CONNECTION_TYPES, type InternetParentType } from '@/types/internetConnection';
import { Globe, ExternalLink } from 'lucide-react';

interface InternetEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId?: string;
  initialData?: {
    label?: string;
    connection_type?: string;
    supplier?: string;
    description?: string;
    external_link?: string;
    owner_id?: string;
    parent_node_id?: string;
    position_x?: number;
    position_y?: number;
    site_id?: string;
    zone_id?: string;
    system_id?: string;
  };
  onSave?: (data: any) => void;
}

export function InternetEditDialog({
  open,
  onOpenChange,
  connectionId,
  initialData,
  onSave,
}: InternetEditDialogProps) {
  const { addConnection, updateConnection, getConnectionById } = useInternetConnectionStore();
  const { sites } = useSiteStore();
  const { zones } = useZoneStore();
  const { systems } = useSystemStore();

  const [label, setLabel] = useState('Internet');
  const [connectionType, setConnectionType] = useState('Fiber');
  const [supplier, setSupplier] = useState('');
  const [description, setDescription] = useState('');
  const [externalLink, setExternalLink] = useState('');
  const [ownerId, setOwnerId] = useState<string | undefined>();
  const [parentType, setParentType] = useState<InternetParentType>('site');
  const [siteId, setSiteId] = useState<string | undefined>();
  const [zoneId, setZoneId] = useState<string | undefined>();
  const [systemId, setSystemId] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  // Filter zones and systems based on selected parent
  const availableZones = useMemo(() => {
    if (!siteId) return zones;
    return zones.filter(z => z.site_id === siteId);
  }, [zones, siteId]);

  const availableSystems = useMemo(() => {
    if (!zoneId) return systems;
    return systems.filter(s => s.zone_id === zoneId);
  }, [systems, zoneId]);

  useEffect(() => {
    if (connectionId) {
      const connection = getConnectionById(connectionId);
      if (connection) {
        setLabel(connection.label);
        setConnectionType(connection.connection_type || 'Fiber');
        setSupplier(connection.supplier || '');
        setDescription(connection.description || '');
        setExternalLink(connection.external_link || '');
        setOwnerId(connection.owner_id || undefined);
        
        // Determine parent type from existing data
        if (connection.system_id) {
          setParentType('system');
          setSystemId(connection.system_id);
          // Find zone and site from system
          const system = systems.find(s => s.id === connection.system_id);
          if (system) {
            setZoneId(system.zone_id);
            const zone = zones.find(z => z.id === system.zone_id);
            if (zone) setSiteId(zone.site_id);
          }
        } else if (connection.zone_id) {
          setParentType('zone');
          setZoneId(connection.zone_id);
          const zone = zones.find(z => z.id === connection.zone_id);
          if (zone) setSiteId(zone.site_id);
        } else {
          setParentType('site');
          setSiteId(connection.site_id || undefined);
        }
      }
    } else if (initialData) {
      setLabel(initialData.label || 'Internet');
      setConnectionType(initialData.connection_type || 'Fiber');
      setSupplier(initialData.supplier || '');
      setDescription(initialData.description || '');
      setExternalLink(initialData.external_link || '');
      setOwnerId(initialData.owner_id || undefined);
      
      if (initialData.system_id) {
        setParentType('system');
        setSystemId(initialData.system_id);
        const system = systems.find(s => s.id === initialData.system_id);
        if (system) {
          setZoneId(system.zone_id);
          const zone = zones.find(z => z.id === system.zone_id);
          if (zone) setSiteId(zone.site_id);
        }
      } else if (initialData.zone_id) {
        setParentType('zone');
        setZoneId(initialData.zone_id);
        const zone = zones.find(z => z.id === initialData.zone_id);
        if (zone) setSiteId(zone.site_id);
      } else if (initialData.site_id) {
        setParentType('site');
        setSiteId(initialData.site_id);
      }
    } else {
      // Reset form for new connection
      setLabel('Internet');
      setConnectionType('Fiber');
      setSupplier('');
      setDescription('');
      setExternalLink('');
      setOwnerId(undefined);
      setParentType('site');
      setSiteId(undefined);
      setZoneId(undefined);
      setSystemId(undefined);
    }
  }, [connectionId, initialData, getConnectionById, open, systems, zones]);

  const handleParentTypeChange = (type: InternetParentType) => {
    setParentType(type);
    // Clear child selections when parent type changes
    if (type === 'site') {
      setZoneId(undefined);
      setSystemId(undefined);
    } else if (type === 'zone') {
      setSystemId(undefined);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        label,
        connection_type: connectionType,
        supplier: supplier || null,
        description: description || null,
        external_link: externalLink || null,
        owner_id: ownerId || null,
        parent_node_id: initialData?.parent_node_id,
        position_x: initialData?.position_x,
        position_y: initialData?.position_y,
        // Set parent IDs based on parent type
        site_id: parentType === 'site' ? siteId : (parentType === 'zone' || parentType === 'system' ? siteId : null),
        zone_id: parentType === 'zone' ? zoneId : (parentType === 'system' ? zoneId : null),
        system_id: parentType === 'system' ? systemId : null,
      };

      if (connectionId) {
        await updateConnection(connectionId, data);
        if (onSave) {
          onSave({ ...data, id: connectionId });
        }
      } else {
        const result = await addConnection(data);
        if (result && onSave) {
          onSave({ ...data, id: result.id });
        }
      }
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-500" />
            {connectionId ? 'Edit Internet Connection' : 'Add Internet Connection'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="label">Name</Label>
            <Input
              id="label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Primary Internet, Backup ISP"
            />
          </div>

          {/* Parent Selection */}
          <div className="space-y-2">
            <Label>Parent Level</Label>
            <Select value={parentType} onValueChange={(v) => handleParentTypeChange(v as InternetParentType)}>
              <SelectTrigger>
                <SelectValue placeholder="Select parent level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="site">Site</SelectItem>
                <SelectItem value="zone">Zone</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Site selector - always shown */}
          <div className="space-y-2">
            <Label>Site</Label>
            <Select value={siteId || ''} onValueChange={setSiteId}>
              <SelectTrigger>
                <SelectValue placeholder="Select site" />
              </SelectTrigger>
              <SelectContent>
                {sites.map(site => (
                  <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Zone selector - shown for zone and system parent types */}
          {(parentType === 'zone' || parentType === 'system') && (
            <div className="space-y-2">
              <Label>Zone</Label>
              <Select value={zoneId || ''} onValueChange={setZoneId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select zone" />
                </SelectTrigger>
                <SelectContent>
                  {availableZones.map(zone => (
                    <SelectItem key={zone.id} value={zone.id}>{zone.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* System selector - shown for system parent type */}
          {parentType === 'system' && (
            <div className="space-y-2">
              <Label>System</Label>
              <Select value={systemId || ''} onValueChange={setSystemId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select system" />
                </SelectTrigger>
                <SelectContent>
                  {availableSystems.map(system => (
                    <SelectItem key={system.id} value={system.id}>{system.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="connectionType">Connection Type</Label>
            <Select value={connectionType} onValueChange={setConnectionType}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {CONNECTION_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="supplier">ISP / Supplier</Label>
            <Input
              id="supplier"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              placeholder="e.g., Telia, Comhem, AT&T"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Additional notes about this connection..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="externalLink">External Link</Label>
            <div className="flex gap-2">
              <Input
                id="externalLink"
                value={externalLink}
                onChange={(e) => setExternalLink(e.target.value)}
                placeholder="https://..."
                className="flex-1"
              />
              {externalLink && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => window.open(externalLink, '_blank')}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <UserSelector
            value={ownerId}
            onChange={setOwnerId}
            label="Owner"
            placeholder="Assign an owner..."
            allowUnassigned
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !label.trim()}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
